import { LayoutDefinition } from "../domain/layouts/LayoutRecord";

export const LAYOUT_NAVIGATION_STATE_KEY = "open-layout-in-visualizer";

/**
 * State used to transport a layout definition to the visualizer.
 */
export interface LayoutNavigationState {
  layoutDefinition: LayoutDefinition;
  schemaVersion: number;
}

export const layoutNavigationState = {
  /**
   * Retrieves the layout definition from the navigation state.
   */
  get: getLayoutFromNavigationState,
  /**
   * Sets the layout definition as navigation state.
   */
  set: setLayoutInNavigationState,
};

function getLayoutFromNavigationState(): LayoutNavigationState | undefined {
  /**
   * We're using session storage to persist the navigation state because
   * react-router's location.state is not available outside the react context.
   *
   * https://github.com/remix-run/react-router/discussions/11374
   */

  const value = sessionStorage.getItem(LAYOUT_NAVIGATION_STATE_KEY);

  if (!value) {
    return undefined;
  }

  try {
    const parsedInput = JSON.parse(value) as unknown;

    if (isLayoutNavigationState(parsedInput)) {
      return parsedInput;
    }
    throw new Error("Workspace input is not a valid WorkspaceInput", {
      cause: parsedInput,
    });
  } catch (e) {
    throw new Error("Failed to parse workspace input from session storage", {
      cause: e,
    });
  } finally {
    // Clear the value from session storage once read
    sessionStorage.removeItem(LAYOUT_NAVIGATION_STATE_KEY);
  }
}

function isLayoutNavigationState(
  input: unknown,
): input is LayoutNavigationState {
  return (
    typeof input === "object" &&
    input !== null &&
    "layoutDefinition" in input &&
    "schemaVersion" in input
  );
}

function setLayoutInNavigationState(input: LayoutNavigationState) {
  sessionStorage.setItem(LAYOUT_NAVIGATION_STATE_KEY, JSON.stringify(input));
}
